import {
  type CensusBambooHrResult,
  authorizeCensusBambooHr,
  getCensusBambooHrResult,
} from "@/api/census-bamboohr-client";
import {
  type CensusConnectStep,
  type UseCensusHrConnectReturn,
  useCensusHrConnect,
} from "@/hooks/use-census-hr-connect";

/**
 * Drives the company-census "Connect BambooHR" step.
 *
 * Same popup machine as QuickBooks (`use-census-hr-connect`), with one
 * difference: `startConnect` takes the employer's BambooHR subdomain. BambooHR's
 * authorize and token URLs are both per-tenant, so the server cannot mint the
 * OAuth URL without it — the roster step collects it in a modal first.
 */

export type CensusBambooStep = CensusConnectStep;

interface UseCensusBambooHrOptions {
  /** Called once the pull has been fetched and mapped. */
  onConnected: (result: CensusBambooHrResult) => void;
  /** Fired on each state transition — used to emit analytics events. */
  onStep?: (
    step: CensusBambooStep,
    meta?: { error?: string; result?: CensusBambooHrResult },
  ) => void;
}

export function useCensusBambooHr(
  opts: UseCensusBambooHrOptions,
): UseCensusHrConnectReturn<string> {
  return useCensusHrConnect<CensusBambooHrResult, string>({
    messageType: "bamboohr-oauth",
    authorize: (companyDomain) => authorizeCensusBambooHr(companyDomain),
    getResult: () => getCensusBambooHrResult(),
    onConnected: opts.onConnected,
    onStep: opts.onStep,
    messages: {
      notConnected: "We couldn't read your BambooHR data. Please try again.",
      resultFailed: "Failed to load your BambooHR data.",
      authorizeFailed: "Failed to start BambooHR authorization.",
      timedOut: "BambooHR didn't finish connecting. Please try again, or add your team manually.",
    },
  });
}
